import {createUseStyles} from 'react-jss';
import {FC, ReactNode, RefObject, useRef} from 'react';
import {colorPalette} from 'styles/colors';

const ARROW_HEIGHT_PX = 19;
const ARROW_WIDTH_PX = 28;

const usePopOverStyles = createUseStyles(
  {
    wrapper: {
      display: 'inline-block',
      height: 0,
      width: 0,
      position: 'relative',

      zIndex: '100',
      filter: 'drop-shadow(0 4px 4px rgba(0, 0, 0, 0.25))',
      cursor: 'auto',
      visibility: 'hidden',
      transitionDelay: '100ms',

      '&:hover': {
        visibility: 'visible',
      },

      '@media print': {
        display: 'none',
      },
    },
    box: {
      boxSizing: 'border-box',
      maxWidth: 'calc(100vw - 100px)',
      padding: '0.5rem',
      position: 'absolute',

      background: colorPalette.yellow025,
      color: 'black',
      fontSize: '1rem',
      fontWeight: '400',
      textAlign: 'center',
      whiteSpace: 'normal',

      '& > p': {
        margin: 0,
        '& > b': {
          display: 'block',
        },
      },
    },
    arrow: {
      width: 0,
      height: 0,
      position: 'relative',

      borderLeft: `${ARROW_WIDTH_PX / 2}px solid transparent`,
      borderRight: `${ARROW_WIDTH_PX / 2}px solid transparent`,
      borderBottom: `${ARROW_HEIGHT_PX}px solid ${colorPalette.yellow025}`,
    },
  },
  {name: 'PopOver'}
);

export const PopOver: FC<{
  id: string;
  children: ReactNode;
  isOpen: boolean;
  attachedRef: RefObject<HTMLElement | null>;
  width?: number;
}> = ({children, isOpen, attachedRef, width, id}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const arrowOffset = (() => {
    if (attachedRef?.current && wrapperRef?.current) {
      return {
        x: attachedRef.current.getBoundingClientRect().right - wrapperRef.current.getBoundingClientRect().left,
        y: attachedRef.current.getBoundingClientRect().bottom - wrapperRef.current.getBoundingClientRect().top,
      };
    } else {
      return {x: 0, y: 0};
    }
  })();

  const boxOffset = (() => {
    if (attachedRef?.current && wrapperRef.current) {
      return {
        x: attachedRef.current.getBoundingClientRect().left - wrapperRef.current.getBoundingClientRect().left,
        y: attachedRef.current.getBoundingClientRect().bottom - wrapperRef.current.getBoundingClientRect().top,
      };
    } else {
      return {x: 0, y: 0};
    }
  })();

  const minWidth = (() => {
    if (attachedRef?.current && wrapperRef.current) {
      return attachedRef.current.clientWidth + ARROW_WIDTH_PX;
    } else {
      return 0;
    }
  })();

  const styles = usePopOverStyles();

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <span
      id={id}
      role='tooltip'
      className={styles.wrapper}
      data-testid='popover'
      ref={wrapperRef}
      onClick={ev => ev.stopPropagation()}
      onKeyDown={ev => ev.stopPropagation()}
      style={isOpen ? {visibility: 'visible', transitionDelay: '0'} : undefined}
    >
      <div
        className={styles.arrow}
        data-testid='popover-arrow'
        style={{
          top: `${arrowOffset.y}px`,
          left: `calc(${arrowOffset.x}px - 1rem)`,
        }}
      />
      <div
        className={styles.box}
        data-testid='popover-box'
        style={{
          width: `${width || '350'}px`,
          minWidth: `max(${minWidth}px, 240px)`,
          top: `${boxOffset.y + ARROW_HEIGHT_PX}px`,
          left: `${boxOffset.x}px`,
        }}
      >
        {children}
      </div>
    </span>
  );
};
